import React, { useState, useCallback, useEffect } from 'react';
import { Input, DatePicker, Spin, Row, Col, Card, Divider, Typography } from 'antd';
import dayjs from 'dayjs';
import './PWH204Form.css';

import { usePWH204Mode } from './hooks/usePWH204Mode';
import { usePWH204Record } from './hooks/usePWH204Record';
import { usePWH204Navigation } from './hooks/usePWH204Navigation';
import ActionBar from './components/ActionBar';
import DetailGrid from './components/DetailGrid';
import LookupField from '../../components/LookupField';
import { lookupApi } from '../../services/lookupApi';
import type { FH140, FH141 } from '../../services/pwh204Api';

const { Text } = Typography;

const EMPTY_MASTER: Partial<FH140> = {
  fh140_01: '',
  fh140_11: new Date().toISOString(),
  fh140_12: '',
  fh140_13: '',
  fh140_14: 0,
  fh140_15: 0,
  fh140_16: '',
  fh140_17: '',
  fh140_18: 0,
  fh140_19: '',
  fh140_80: 'N',
  fh140_89: '',
  fh140_90: 0,
  fh140_91: '',
  fh140_93: '',
};

const PWH204Form: React.FC = () => {
  const { mode, toView, toAdd, toEdit, isEditing } = usePWH204Mode();
  const { record, loading, saving, load, save, remove, setRecord } = usePWH204Record();
  const { list, hasPrev, hasNext, fetchList, goFirst, goLast, goPrev, goNext, setCurrentById } =
    usePWH204Navigation();

  const [formData, setFormData] = useState<Partial<FH140>>({ ...EMPTY_MASTER });
  const [details, setDetails] = useState<Partial<FH141>[]>([]);

  // Sync record to form
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    }
  }, [record]);

  // Compute display totals from details
  const totalNet = details.reduce((s, d) => s + (d.fh141_13 || 0), 0);
  const totalTax = details.reduce((s, d) => s + (d.fh141_14 || 0), 0);
  const totalAmount = totalNet + totalTax;

  const loadRecord = useCallback(async (id: string) => {
    const result = await load(id);
    if (result) setCurrentById(id);
    return result;
  }, [load, setCurrentById]);

  // Navigation
  const handleFirst = useCallback(async () => {
    const id = goFirst(); if (id) await loadRecord(id);
  }, [goFirst, loadRecord]);

  const handlePrev = useCallback(async () => {
    const id = goPrev(); if (id) await loadRecord(id);
  }, [goPrev, loadRecord]);

  const handleNext = useCallback(async () => {
    const id = goNext(); if (id) await loadRecord(id);
  }, [goNext, loadRecord]);

  const handleLast = useCallback(async () => {
    const id = goLast(); if (id) await loadRecord(id);
  }, [goLast, loadRecord]);

  // Mode transitions
  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER, fh140_11: new Date().toISOString() });
    setDetails([]);
    toAdd();
  }, [toAdd]);

  const handleEdit = useCallback(() => {
    toEdit();
  }, [toEdit]);

  const handleCancel = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    } else {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
    }
    toView();
  }, [record, toView]);

  const handleSave = useCallback(async () => {
    const masterToSave = {
      ...formData,
      fh140_14: totalNet,
      fh140_15: totalTax,
      fh140_18: details.length,
    };
    const result = await save(mode as 'add' | 'edit', masterToSave, details);
    if (result) {
      toView();
      await fetchList();
      setCurrentById(result.fh140_01);
    }
  }, [formData, details, totalNet, totalTax, mode, save, toView, fetchList, setCurrentById]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    const ok = await remove(record.fh140_01);
    if (ok) {
      toView();
      await fetchList();
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
    }
  }, [record, remove, toView, fetchList]);

  const handleFieldChange = useCallback(<K extends keyof FH140>(field: K, value: FH140[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Lookup: resolve company name when company code is confirmed
  const handleCompanyCodeConfirm = useCallback(async (code: string) => {
    if (!code.trim()) {
      setFormData(prev => ({ ...prev, fh140_13: '' }));
      return;
    }
    try {
      const result = await lookupApi.getName('supplier', code);
      setFormData(prev => ({ ...prev, fh140_13: result.name || '' }));
    } catch {
      setFormData(prev => ({ ...prev, fh140_13: '*查無此代號*' }));
    }
  }, []);

  // Load initial list on mount
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return (
    <div className="pwh204-form">
      <ActionBar
        mode={mode}
        hasRecord={!!record}
        hasPrev={hasPrev}
        hasNext={hasNext}
        saving={saving}
        canAdd={true}
        canEdit={true}
        canDelete={true}
        onFirst={handleFirst}
        onPrev={handlePrev}
        onNext={handleNext}
        onLast={handleLast}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />

      <Spin spinning={loading}>
        <Card
          title="進貨折讓單"
          className="pwh204-main-card"
          extra={
            formData.fh140_80 === 'P' && (
              <Text type="danger" strong>已核准</Text>
            )
          }
        >
          {/* Header Section */}
          <Row gutter={[16, 8]} className="pwh204-header">
            <Col span={6}>
              <div className="field-row">
                <label>折讓單號</label>
                <Input
                  value={formData.fh140_01 || '（新增後自動產生）'}
                  readOnly
                  disabled
                  className="field-input"
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="field-row">
                <label>折讓日期</label>
                <DatePicker
                  value={formData.fh140_11 ? dayjs(formData.fh140_11) : null}
                  disabled={!isEditing}
                  style={{ width: '100%' }}
                  onChange={(d) => handleFieldChange('fh140_11', d ? d.toISOString() : null as any)}
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="field-row">
                <label>公司代號</label>
                <LookupField
                  lookupType="supplier"
                  value={formData.fh140_12 || ''}
                  onChange={code => handleFieldChange('fh140_12', code)}
                  onCodeConfirm={handleCompanyCodeConfirm}
                  disabled={!isEditing}
                  maxLength={12}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="field-row">
                <label>公司名稱</label>
                <Input
                  value={formData.fh140_13 || ''}
                  readOnly
                  disabled
                />
              </div>
            </Col>
          </Row>

          {/* Summary Row */}
          <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
            <Col span={6}>
              <div className="field-row">
                <label>折讓總淨額</label>
                <Input
                  value={totalNet.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}
                  readOnly disabled
                  style={{ textAlign: 'right' }}
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="field-row">
                <label>折讓總稅額</label>
                <Input
                  value={totalTax.toLocaleString('zh-TW')}
                  readOnly disabled
                  style={{ textAlign: 'right' }}
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="field-row">
                <label>折讓總金額</label>
                <Input
                  value={totalAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}
                  readOnly disabled
                  style={{ textAlign: 'right' }}
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="field-row">
                <label>總項數</label>
                <Input value={details.length} readOnly disabled style={{ textAlign: 'right' }} />
              </div>
            </Col>
          </Row>

          <Divider />

          {/* Detail Grid */}
          <DetailGrid
            details={details}
            isEditing={isEditing}
            onChange={setDetails}
          />

          {/* Remark /備注 */}
          <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
            <Col span={24}>
              <div className="field-row">
                <label>備注</label>
                <Input.TextArea
                  value={formData.fh140_19 || ''}
                  readOnly={!isEditing}
                  disabled={!isEditing}
                  rows={2}
                  onChange={e => handleFieldChange('fh140_19', e.target.value)}
                />
              </div>
            </Col>
          </Row>
        </Card>
      </Spin>
    </div>
  );
};

export default PWH204Form;
